import {
  DefectMediaViewSchema,
  TaskCommentViewSchema,
  TaskExecutionViewSchema,
  TaskViewSchema,
} from '@fsf/types';
import { _ } from '@ngx-translate/core';
import * as z from 'zod';

export const TASK_EXECUTION_STATUSES = [
  'open',
  'in_progress',
  'done',
  'accepted',
] as const;
export type TaskExecutionStatus = (typeof TASK_EXECUTION_STATUSES)[number];

export const RECURRENCE_UNITS = ['hour', 'day', 'week', 'month'] as const;
export type RecurrenceUnit = (typeof RECURRENCE_UNITS)[number];

/** Approximate unit length in hours (a month counts as 30 days), as in RecurrenceUnit::hours(). */
export const RECURRENCE_UNIT_HOURS: Record<RecurrenceUnit, number> = {
  hour: 1,
  day: 24,
  week: 7 * 24,
  month: 30 * 24,
};

const TaskCommentSchema = TaskCommentViewSchema;
export type TaskComment = z.infer<typeof TaskCommentSchema>;

const TaskMediaSchema = DefectMediaViewSchema;
export type TaskMedia = z.infer<typeof TaskMediaSchema>;

const TaskExecutionSchema = TaskExecutionViewSchema;
export type TaskExecution = z.infer<typeof TaskExecutionSchema>;

const TaskSchema = TaskViewSchema.extend({
  executions: z.array(TaskExecutionSchema),
  comments: z.array(TaskCommentSchema),
  media: z.array(TaskMediaSchema),
  // FE-only flat projections, populated by TaskService.
  // Enable PrimeNG sort/filter/global-search without nested-field handling.
  responsible_user_name: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  due_at: z.string().nullable().optional(),
  missed_count: z.number().optional(),
  is_overdue: z.boolean().optional(),
});

export type Task = z.infer<typeof TaskSchema>;

export const taskTranslations = _([
  'task.title',
  'task.description',
  'task.status',
  'task.priority',
  'task.dueAt',
  'task.estimatedTime',
  'task.responsibleUser',
  'task.notAssigned',
  'task.missed',
  'task.recurrence.label',
  'task.statuses.open',
  'task.statuses.in_progress',
  'task.statuses.done',
  'task.statuses.accepted',
  'task.recurrence.units.hour',
  'task.recurrence.units.day',
  'task.recurrence.units.week',
  'task.recurrence.units.month',
  'task.recurrence.every.hour',
  'task.recurrence.every.day',
  'task.recurrence.every.week',
  'task.recurrence.every.month',
  'task.recurrence.single.hour',
  'task.recurrence.single.day',
  'task.recurrence.single.week',
  'task.recurrence.single.month',
  'task.tabs.open',
  'task.tabs.done',
  'task.tabs.all',
  'task.groups.overdue',
  'task.groups.today',
  'task.groups.tomorrow',
  'task.groups.thisWeek',
  'task.groups.later',
  'task.groups.noDue',
  'task.groups.doneToday',
  'task.groups.finished',
  'task.timeline.due',
  'task.timeline.completed',
  'task.timeline.completedMissed',
  'task.timeline.approved',
  'task.created',
  'task.createError',
  'task.updated',
  'task.updateError',
  'task.completed',
  'task.statusUpdated',
  'task.loadError',
  'task.commentAddError',
  'task.commentDeleteError',
  'task.mediaDeleteError',
  'task.uploadError',
  'task.yes',
  'task.no',
]);

// Input schemas (→ StoreTaskRequest / UpdateTaskRequest)
export const StoreTaskSchema = z
  .object({
    title: z.string().min(1).max(255),
    description: z.string().nullable().optional(),
    priority: z
      .enum(['low', 'medium', 'high', 'critical'])
      .nullable()
      .optional(),
    // Geschätzter Aufwand in Stunden
    estimated_time: z.number().min(0).nullable().optional(),
    responsible_user_id: z.number().nullable().optional(),
    requires_approval: z.boolean().optional(),
    recurrence_unit: z.enum(RECURRENCE_UNITS).nullable().optional(),
    recurrence_every: z.number().int().min(1).max(1000).nullable().optional(),
    due_at: z.string().datetime().nullable().optional(),
  })
  .refine((v) => !v.recurrence_unit || !!v.recurrence_every, {
    path: ['recurrence_every'],
    message: 'validation.required',
  })
  .refine(
    (v) =>
      !v.recurrence_unit ||
      !v.recurrence_every ||
      v.estimated_time == null ||
      RECURRENCE_UNIT_HOURS[v.recurrence_unit] * v.recurrence_every >=
        v.estimated_time,
    {
      path: ['recurrence_every'],
      message: _('task.recurrence.shorterThanEstimate'),
    },
  );
export const UpdateTaskSchema = StoreTaskSchema;
